import { message, TableProps } from "antd"
import { ICampaignResult } from "../types/sbCampaignApi.types"
import { campaignStates } from "../../../types/campaigns"
import SwitchCampaignState from "../../../ui/SwitchCampaignState"
import useSearchColumn from "../../../hooks/useSearchColumn"
import { columnsWidth } from "../constants/columns.constants"
import { useMemo } from "react"
import sbCampaignApi from "../api/sbCampaign.api"
import { setStateType } from "../../../types/setStateType"

export const useCampaignsColumns = (
  setCampaignDataForTable: setStateType<ICampaignResult[] | null>
): TableProps<ICampaignResult>["columns"] => {
  const searchColumnCampaignName = useSearchColumn<ICampaignResult>({ dataIndex: "campaignName" })

  function changeCampaignState(value: boolean, campaignId: string) {
    const stateByCampaignId = [
      {
        campaignId: campaignId,
        state: value ? campaignStates.ENABLED : campaignStates.PAUSED,
      },
    ]

    sbCampaignApi
      .updateCampaignsState({
        stateByCampaignIds: stateByCampaignId,
      })
      .then((res) => {
        if (res.status !== 200) {
          message.error("Failed to update campaign status")
          return
        }

        setCampaignDataForTable((prevCampaigns) => {
          return prevCampaigns
            ? prevCampaigns.map((campaign) => {
                if (campaign.campaignId === campaignId) {
                  return {
                    ...campaign,
                    campaignStatus: value ? campaignStates.ENABLED : campaignStates.PAUSED,
                  }
                }

                return campaign
              })
            : null
        })
      })
      .catch(() => {
        message.error("Failed to update campaign status")
      })
  }

  const columns: TableProps<ICampaignResult>["columns"] = useMemo(
    () => [
      {
        title: "Target ACOS",
        render: (target) => "-",
        align: "center",
        width: columnsWidth[0],
      },
      {
        title: "Active",
        dataIndex: "campaignStatus",
        render: (campaignStatus: campaignStates, row: ICampaignResult) => (
          <SwitchCampaignState
            onChange={(value) => changeCampaignState(value, row.campaignId)}
            campaignState={campaignStatus}
          />
        ),
        filters: [
          { text: "Active", value: campaignStates.ENABLED },
          { text: "Paused", value: campaignStates.PAUSED },
        ],
        onFilter: (value, record) => record.campaignStatus === value,
        width: columnsWidth[1],
        align: "center",
      },
      {
        title: "Campaigns",
        dataIndex: "campaignName",
        key: "campaignName",
        width: columnsWidth[2],
        ...searchColumnCampaignName,
      },
      {
        title: "Target CPC in amazon",
        key: "targetCpcInAmazon",
        width: columnsWidth[3],
        align: "center",
        render: (row) => "-",
      },
      {
        title: "Target CPC",
        key: "targetCPC",
        width: columnsWidth[4],
        align: "center",
        render: (row) => "-",
      },
      {
        title: "Best CPC",
        key: "bestCPC",
        width: columnsWidth[5],
        align: "center",
        render: (row) => "-",
      },
      {
        title: "Limited CPC",
        key: "limitedCPC",
        width: columnsWidth[6],
        align: "center",
        render: (row) => "-",
      },
      {
        title: "Deflated CPC",
        key: "deflatedCPC",
        width: columnsWidth[7],
        align: "center",
        render: (row) => "-",
      },
      {
        title: "Impressions",
        dataIndex: "impressions",
        key: "impressions",
        width: columnsWidth[8],
        align: "start",
        sorter: (a, b) => a.impressions - b.impressions,
        render: (impressions) => impressions,
      },
      {
        title: "Clicks",
        dataIndex: "clicks",
        key: "clicks",
        width: columnsWidth[9],
        align: "start",
        sorter: (a, b) => a.clicks - b.clicks,
        render: (clicks) => clicks,
      },
      {
        title: "CTR",
        dataIndex: "ctr",
        key: "ctr",
        width: columnsWidth[10],
        align: "center",
        sorter: (a, b) => a.ctr - b.ctr,
        render: (ctr) => ctr + "%",
      },
      {
        title: "Spend",
        dataIndex: "spend",
        key: "spend",
        width: columnsWidth[11],
        align: "center",
        sorter: (a, b) => a.spend - b.spend,
        render: (spend) => "$" + spend,
      },
      {
        title: "Sales",
        dataIndex: "sales",
        key: "sales",
        width: columnsWidth[12],
        align: "center",
        sorter: (a, b) => a.sales - b.sales,
        render: (sales) => "$" + sales,
      },
      {
        title: "Orders",
        dataIndex: "orders",
        key: "orders",
        width: columnsWidth[13],
        align: "end",
        sorter: (a, b) => a.orders - b.orders,
        render: (orders) => orders,
      },
      {
        title: "Units",
        dataIndex: "units",
        key: "units",
        width: columnsWidth[14],
        align: "end",
        sorter: (a, b) => a.units - b.units,
        render: (units) => units,
      },
      {
        title: "CVR",
        dataIndex: "cvr",
        key: "cvr",
        width: columnsWidth[15],
        align: "center",
        sorter: (a, b) => a.cvr - b.cvr,
        render: (cvr) => cvr + "%",
      },
      {
        title: "ACOS",
        dataIndex: "acos",
        key: "acos",
        width: columnsWidth[16],
        align: "center",
        sorter: (a, b) => a.acos - b.acos,
        render: (acos) => acos + "%",
      },
      {
        title: "CPC",
        dataIndex: "cpc",
        key: "cpc",
        width: columnsWidth[17],
        align: "center",
        sorter: (a, b) => a.cpc - b.cpc,
        render: (cpc) => (cpc == null ? "-" : "$" + cpc),
      },
      {
        title: "ROAS",
        dataIndex: "roas",
        key: "roas",
        width: columnsWidth[18],
        align: "center",
        render: (roas) => (roas ? roas : "-"),
      },
    ],
    [searchColumnCampaignName]
  )

  return columns
}
